import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  inputAdornmentRecipe,
  inputRecipe,
  textareaRecipe,
  type InputRecipeProps,
} from '../recipes/input.recipe';
import { useFieldControlProps } from './field-context';
import { formatTime, parseTypedTime } from './time-format';

type ControlSize = NonNullable<InputRecipeProps['size']>;

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * `cell` for a value flush inside a box something else draws — a grid
   * cell, a stepper. No chrome of its own, centred, colour from the parent.
   */
  variant?: InputRecipeProps['variant'];
  size?: ControlSize;
  /** Icon rendered inside the left edge — a magnifier on a search box. */
  leadingIcon?: React.ReactNode;
  /** Icon or short unit rendered inside the right edge. */
  trailingIcon?: React.ReactNode;
}

/**
 * Single-line text entry.
 *
 * Inside a Field it inherits the id, disabled, required and invalid state
 * automatically, so the usual case is just `<Input {...register('name')} />`.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      className,
      variant = 'default',
      size = 'md',
      type = 'text',
      leadingIcon,
      trailingIcon,
      id,
      disabled,
      required,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) {
    const fieldProps = useFieldControlProps({
      id,
      disabled,
      required,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    });

    const controlClassName = cn(
      inputRecipe({
        variant,
        size,
        hasLeadingIcon: Boolean(leadingIcon),
        hasTrailingIcon: Boolean(trailingIcon),
      }),
      className
    );

    const control =
      type === 'time' ? (
        <TypedTimeInput
          ref={ref}
          className={controlClassName}
          {...fieldProps}
          {...props}
        />
      ) : (
        <input
          ref={ref}
          type={type}
          className={controlClassName}
          {...fieldProps}
          {...props}
        />
      );

    // One shape, always. Returning a bare `control` when there is no adornment
    // and a wrapper when there is means React cannot reconcile between the two:
    // it drops the live input node and mounts a replacement, which loses focus,
    // the caret, and any in-progress IME composition. An input that gains a
    // spinner while you type — Autocomplete — did exactly that mid-word. With
    // no adornment the wrapper is `contents`, so it takes part in no layout and
    // the input sits in its parent's flow exactly as the bare element did.
    const adorned = Boolean(leadingIcon || trailingIcon);

    return (
      <div
        className={cn(
          'relay-ui-internal',
          adorned ? 'relative w-full' : 'contents'
        )}
      >
        {leadingIcon ? (
          <span className={inputAdornmentRecipe({ side: 'leading', size })}>
            {leadingIcon}
          </span>
        ) : null}
        {control}
        {trailingIcon ? (
          <span className={inputAdornmentRecipe({ side: 'trailing', size })}>
            {trailingIcon}
          </span>
        ) : null}
      </div>
    );
  }
);

/**
 * `type="time"`, typed rather than picked. The native time input's clock
 * dropdown made people scroll two columns to set a time they could type in
 * four keystrokes, and hiding its icon was not enough: a dispatcher's Chrome
 * still opened it on a click into Dispatch Time. A text field has no picker
 * to open.
 *
 * Callers see what the native input gave them: a 24-hour "HH:mm" value, ''
 * when empty, and a change event whose target carries that value and the
 * field's name. The event's target is a hidden input holding the value, which
 * also keeps the field in a plain form post.
 *
 * The time is sent once, when the dispatcher leaves the field or presses
 * Enter, not on every key. Sent as they typed, a caller that will not take ''
 * (Edit Job's Dispatch Time keeps its time) had the box fill back in the
 * moment it was cleared, and holding Backspace sent "0", midnight, on the way
 * down.
 */
const TypedTimeInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function TypedTimeInput(
  {
    value,
    defaultValue,
    name,
    onChange,
    onBlur,
    onKeyDown,
    placeholder,
    ...props
  },
  ref
) {
  const [uncontrolled, setUncontrolled] = React.useState(
    String(defaultValue ?? '')
  );
  const current = value === undefined ? uncontrolled : String(value);

  // What is in the box. It is the dispatcher's own text while they type, and
  // is redrawn from the value only when the value changes, or after a send:
  // `drawn` is then null, so the next render draws whatever the caller kept,
  // which is the old time when it would not take the new one.
  const [draft, setDraft] = React.useState(() => formatTime(current));
  const [drawn, setDrawn] = React.useState<string | null>(current);
  if (current !== drawn) {
    setDrawn(current);
    setDraft(formatTime(current));
  }

  const hidden = React.useRef<HTMLInputElement>(null);

  function send(event: React.SyntheticEvent<HTMLInputElement>) {
    const next = parseTypedTime(draft);
    // Half a time ("14:3") is not kept: the box goes back to the value.
    if (next === null || next === current || !hidden.current) {
      setDraft(formatTime(current));
      return;
    }
    if (value === undefined) setUncontrolled(next);
    setDrawn(null);
    hidden.current.value = next;
    // The event itself, read through the hidden input: the caller's
    // e.target.value is "HH:mm", and preventDefault and the rest still work,
    // which they would not on a spread copy.
    onChange?.(
      Object.create(event, {
        target: { value: hidden.current },
        currentTarget: { value: hidden.current },
      })
    );
  }

  return (
    <>
      <input
        ref={ref}
        type="text"
        inputMode="text"
        autoComplete="off"
        maxLength={8}
        placeholder={placeholder ?? 'hh:mm AM'}
        value={draft}
        onChange={(event) =>
          setDraft(event.target.value.replace(/[^\d:apmAPM ]/g, ''))
        }
        onKeyDown={(event) => {
          // Before the form's own Enter handling, so a submit sees the time.
          if (event.key === 'Enter') send(event);
          onKeyDown?.(event);
        }}
        onBlur={(event) => {
          send(event);
          onBlur?.(event);
        }}
        {...props}
      />
      <input ref={hidden} type="hidden" name={name} value={current} />
    </>
  );
});

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  size?: ControlSize;
}

/** Multi-line text entry. Same Field integration as Input. */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      className,
      size = 'md',
      rows = 3,
      id,
      disabled,
      required,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) {
    const fieldProps = useFieldControlProps({
      id,
      disabled,
      required,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    });

    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(textareaRecipe({ size }), className)}
        {...fieldProps}
        {...props}
      />
    );
  }
);
